import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Menu,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  MenuItem,
  FormControlLabel,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Delete, DeleteSweep, DoneAll, WifiOff } from '@mui/icons-material';
import { showConfirmDialogComponent } from './ConfirmDialog';
import {
  INotification,
  INotificationContentItem,
  INotifcationAction,
  INotificationSettings,
} from '../Interfaces/INotification';
import WebSocketManagerInstace from '../services/websocket.service';

export const Notifications = () => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [notificationSettings, setNotificationSettings] =
    useState<INotificationSettings | null>(null);
  const [showNotificationsAtStartUpDone, setShowNotificationsAtStartUpDone] =
    useState(false);
  const [notificationStartupCooldown, setNotificationStartupCooldown] =
    useState(
      isNaN(
        new Date(
          String(localStorage.getItem('notification_startup_cooldown'))
        ).getTime()
      )
        ? null
        : new Date(
            String(localStorage.getItem('notification_startup_cooldown'))
          )
    );
  const [selectedCoolDown, setSelectedCoolDown] = useState(1);

  const [showOnlyUnread, setShowOnlyUnread] = useState(true);

  const hasNoCoolDown = (() => {
    if (notificationStartupCooldown === null) return true;

    const cooldownDate = new Date(notificationStartupCooldown);
    const today = new Date();

    cooldownDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return cooldownDate <= today;
  })();

  const sortNotifications = useCallback(
    (a: INotification, b: INotification) => {
      return (
        new Date(b.Created_at).getTime() - new Date(a.Created_at).getTime()
      );
    },
    []
  );

  const updateNotifications = useCallback(
    (newNotifications: INotification[]) => {
      setNotifications(prevNotifications => {
        const filteredNotifications = prevNotifications.filter(
          n =>
            !newNotifications.some(
              newNotification =>
                n.idNotification === newNotification.idNotification
            )
        );
        let notis = [...filteredNotifications, ...newNotifications].sort(
          sortNotifications
        );

        notis = notis.map(notification => {
          const newNotification = newNotifications.find(
            newNotification =>
              newNotification.idNotification === notification.idNotification
          );
          if (
            newNotification &&
            newNotification.isRead !== notification.isRead
          ) {
            return { ...notification, isRead: newNotification.isRead };
          }
          return notification;
        });
        return notis;
      });
    },
    [sortNotifications]
  );

  const handleNotification = useCallback(
    (notification: INotification | INotification[]) => {
      if (Array.isArray(notification)) {
        updateNotifications(notification);
      } else {
        updateNotifications([notification]);
      }
    },
    [updateNotifications]
  );

  const handleSubscriptionChange = useCallback(
    (notificationSettings: INotificationSettings) => {
      const notificationOptionArray = notificationSettings.NotificationOptions;
      const notificationSocket =
        WebSocketManagerInstace.getSocketService('notification');
      if (!notificationSocket) {
        return;
      }

      setNotificationSettings(prevNotifications => {
        if (
          prevNotifications?.NotificationOptions !==
          notificationSettings.NotificationOptions
        ) {
          const prevTopics = prevNotifications?.NotificationOptions || [];

          const newTopics = notificationOptionArray.filter(
            topic => !prevTopics.includes(topic)
          );
          const oldTopics = prevTopics.filter(
            topic => !notificationOptionArray.includes(topic)
          );

          newTopics.forEach(topic => {
            notificationSocket.subscribe_to_topic(
              topic.toString(),
              handleNotification
            );
          });
          notificationSocket.sendMessage(
            'get_all_notifications_for_topics',
            newTopics,
            (message: INotification[]) => {
              handleNotification(message);
              if (
                !showNotificationsAtStartUpDone &&
                notificationSettings.ShowNotificationsAtLogin &&
                message.filter(notification => !notification.isRead).length > 0
              ) {
                setShowDialog(true);
                setShowNotificationsAtStartUpDone(true);
              }
            }
          );

          oldTopics.forEach(topic => {
            notificationSocket.unsubscribe_from_topic(topic.toString());
          });
        }
        return notificationSettings;
      });

      setNotifications(prevNotifications =>
        prevNotifications.filter(notification =>
          notificationOptionArray.includes(notification.idTopic)
        )
      );
    },
    [handleNotification]
  );

  useEffect(() => {
    WebSocketManagerInstace.getSocketService(
      'notification',
      (connected: boolean) => {
        setIsConnected(connected);
        if (!connected) {
          setShowSnackbar(true);
          setNotificationSettings(null);
          setNotifications([]);
        }
      }
    );

    return () => {
      WebSocketManagerInstace.removeSocketService('notification');
    };
  }, [handleNotification]);

  useEffect(() => {
    if (isConnected) {
      const notificationSocket =
        WebSocketManagerInstace.getSocketService('notification');
      if (!notificationSocket) {
        return;
      }
      notificationSocket.sendEvent(
        'get_usertopics',
        handleSubscriptionChange,
        false
      );
    }
  }, [isConnected, handleNotification, handleSubscriptionChange]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (idNotification: number) => {
    const notificationSocket =
      WebSocketManagerInstace.getSocketService('notification');
    if (!notificationSocket) {
      return;
    }
    notificationSocket.sendMessage(
      'mark_notification_as_read',
      idNotification,
      () => {}
    );
    setNotifications(prevNotifications =>
      prevNotifications.map(notification =>
        notification.idNotification === idNotification
          ? { ...notification, isRead: true }
          : notification
      )
    );
  };

  // Bulk-Aktionen über die vorhandenen Einzel-Handler des Websocket-Backends —
  // so bleiben auch andere offene Tabs über den Rück-Push synchron.
  const handleMarkAllAsRead = () => {
    const notificationSocket =
      WebSocketManagerInstace.getSocketService('notification');
    if (!notificationSocket) {
      return;
    }
    notifications
      .filter(notification => !notification.isRead)
      .forEach(notification =>
        notificationSocket.sendMessage(
          'mark_notification_as_read',
          notification.idNotification,
          () => {}
        )
      );
    setNotifications(prevNotifications =>
      prevNotifications.map(notification => ({ ...notification, isRead: true }))
    );
  };

  const handleDeleteAll = async () => {
    const confirmed = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie alle Benachrichtigungen löschen wollen?',
      'Löschen',
      'Abbrechen',
      theme
    );
    if (!confirmed) return;

    const notificationSocket =
      WebSocketManagerInstace.getSocketService('notification');
    if (!notificationSocket) {
      return;
    }
    notifications.forEach(notification =>
      notificationSocket.sendMessage(
        'delete_notification',
        notification.idNotification,
        () => {}
      )
    );
    setNotifications([]);
  };

  const handleNotificationClick = (notification: INotification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.idNotification);
    }
    if (notification.Action === INotifcationAction.GOTO) {
      window.open(
        `/${notification.ReferenceType}/${notification.idReference}`,
        '_blank'
      );
    }
  };

  const renderNotificationContent = (content: INotificationContentItem[]) => {
    return content.map((item, index) => {
      if (item.type === 'text') {
        return <Typography key={index}>{item.value}</Typography>;
      } else if (item.type === 'list') {
        return (
          <ul key={index}>
            {item.items?.map((listItem, idx) => (
              <li key={idx}>{listItem}</li>
            ))}
          </ul>
        );
      } else if (item.type === 'linebreak') {
        return <br key={index} />;
      } else {
        return null;
      }
    });
  };

  const unreadCount = notifications.filter(
    notification => !notification.isRead
  ).length;

  const handleCloseDialog = () => {
    const coolDown = new Date();
    coolDown.setDate(coolDown.getDate() + selectedCoolDown);
    localStorage.setItem(
      'notification_startup_cooldown',
      coolDown.toISOString().split('T')[0]
    );
    setShowDialog(false);
  };

  const handleDeleteNotification = (idNotification: number) => {
    const notificationSocket =
      WebSocketManagerInstace.getSocketService('notification');
    if (!notificationSocket) {
      return;
    }
    notificationSocket.sendMessage(
      'delete_notification',
      idNotification,
      () => {}
    );
    setNotifications(prevNotifications =>
      prevNotifications.filter(
        notification => notification.idNotification !== idNotification
      )
    );
  };

  useEffect(() => {
    if (
      notifications.filter(notification => !notification.isRead).length === 0 &&
      showDialog
    ) {
      setShowDialog(false);
    }
  }, [notifications, showDialog]);

  if (!isConnected) {
    return (
      <Box>
        <IconButton
          color='inherit'
          onClick={handleMenuOpen}
          title='Meine Benachrichtigungen'
        >
          <Badge
            badgeContent={'!'}
            color='warning'
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <WifiOff />
          </Badge>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <List>
            <ListItem>
              <ListItemText primary='Verbindung wird hergestellt...' />
            </ListItem>
          </List>
        </Menu>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowSnackbar(false)}
        >
          <Alert onClose={() => setShowSnackbar(false)} severity='error'>
            Der Benachrichtigungsdienst hat keine Verbindung zum Server.
          </Alert>
        </Snackbar>
      </Box>
    );
  } else {
    return (
      <Box>
        <IconButton
          color='inherit'
          onClick={handleMenuOpen}
          title='Meine Benachrichtigungen'
          sx={{
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color='secondary'
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                padding: '0 6px',
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 4,
            sx: {
              borderRadius: 3,
              paddingBottom: 1,
              minWidth: 420,
              maxHeight: '75vh',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant='h6' fontWeight={600}>
              Meine Benachrichtigungen
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={!showOnlyUnread}
                    onChange={e => setShowOnlyUnread(!e.target.checked)}
                  />
                }
                label='Alle'
              />
              <Chip
                size='small'
                label={`${notifications.filter(not => !not.isRead).length} Benachrichtigungen`}
              />
              <Tooltip title='Alle als gelesen markieren' arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    <DoneAll fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Alle löschen' arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={handleDeleteAll}
                    disabled={notifications.length === 0}
                  >
                    <DeleteSweep fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <List sx={{ minWidth: '500px', p: 2 }}>
            {notifications.length > 0 &&
              (showOnlyUnread
                ? notifications.filter(notification => !notification.isRead)
                : notifications
              ).map((notification, index) => (
                <React.Fragment key={notification.idNotification}>
                  <ListItem
                    alignItems='flex-start'
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      backgroundColor: notification.isRead
                        ? 'background.paper'
                        : 'rgba(33,150,243,0.12)',
                      borderRadius: 2,
                      mb: 1.5,
                      px: 2,
                      py: 1.5,
                      boxShadow: 1,
                      transition: '0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: notification.isRead
                            ? 'grey.300'
                            : 'primary.main',
                        }}
                      >
                        <NotificationsIcon />
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Typography variant='subtitle1' fontWeight={600}>
                          {notification.Title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Box mt={0.5}>
                            {renderNotificationContent(notification.Content)}
                          </Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {new Date(notification.Created_at).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />

                    <IconButton
                      size='small'
                      onClick={event => {
                        // Nur löschen — der Zeilen-Klick (Öffnen) darf nicht
                        // mitfeuern.
                        event.stopPropagation();
                        handleDeleteNotification(notification.idNotification);
                      }}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </ListItem>
                  {index < notifications.length - 1 && (
                    <Divider variant='inset' component='li' light />
                  )}
                </React.Fragment>
              ))}
            {notifications.length === 0 && (
              <ListItem>
                <ListItemText primary='Keine Benachrichtigungen vorhanden' />
              </ListItem>
            )}
          </List>
        </Menu>

        <Dialog
          open={hasNoCoolDown && showDialog && unreadCount > 0}
          onClose={handleCloseDialog}
          PaperProps={{
            sx: {
              borderRadius: 4,
              padding: 1,
            },
          }}
        >
          <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
            Ungelesene Benachrichtigungen
          </DialogTitle>

          <DialogContent dividers>
            <List>
              {notifications
                .filter(n => !n.isRead)
                .map(notification => (
                  <ListItem
                    alignItems='flex-start'
                    onClick={() => {
                      handleCloseDialog();
                      handleNotificationClick(notification);
                    }}
                    sx={{
                      backgroundColor: notification.isRead
                        ? 'background.paper'
                        : 'rgba(33,150,243,0.12)',
                      borderRadius: 2,
                      mb: 1.5,
                      px: 2,
                      py: 1.5,
                      boxShadow: 1,
                      transition: '0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <NotificationsIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight={600}>
                          {notification.Title}
                        </Typography>
                      }
                      secondary={
                        <>
                          {renderNotificationContent(notification.Content)}
                          <Typography variant='caption' color='text.secondary'>
                            {new Date(notification.Created_at).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <TextField
              label='Nächste Erinnerung'
              size='small'
              select
              sx={{
                minWidth: 180,
                '& .MuiInputBase-root': { borderRadius: 2 },
              }}
              value={String(selectedCoolDown)}
              onChange={e => setSelectedCoolDown(Number(e.target.value))}
            >
              <MenuItem value={1}>Morgen</MenuItem>
              <MenuItem value={2}>Übermorgen</MenuItem>
              <MenuItem value={3}>In drei Tagen</MenuItem>
              <MenuItem value={7}>Nächste Woche</MenuItem>
            </TextField>

            <Button
              variant='contained'
              onClick={handleCloseDialog}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Schließen
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowSnackbar(false)}
        >
          <Alert onClose={() => setShowSnackbar(false)} severity='error'>
            Der Benachrichtigungsdienst hat keine Verbindung zum Server.
          </Alert>
        </Snackbar>
      </Box>
    );
  }
};
